import type { Conversation, BrokerLite } from './types';
import { initials, relativeTime, truncate, displayName } from './convUtils';
import ChannelIcon from './ChannelIcon';
import PersonaChip from './PersonaChip';

export default function ConversationListItem({
  c,
  selected,
  onSelect,
  brokers,
}: {
  c: Conversation;
  selected: boolean;
  onSelect: (id: string) => void;
  brokers?: Record<string, BrokerLite>;
}) {
  const name = displayName(c.contact_name || c.phone);
  const hasUnread = c.unread_count > 0;
  const owner = c.owner_id && brokers ? brokers[c.owner_id] : null;

  return (
    <button
      type="button"
      onClick={() => onSelect(c.id)}
      className={`w-full text-left flex gap-3 px-3 py-2.5 border-b border-gray-100 transition-colors hover:bg-gray-50 ${
        selected ? 'bg-primary-50 border-l-2 border-l-primary-600' : 'border-l-2 border-l-transparent'
      }`}
    >
      <div
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold ${
          selected ? 'bg-primary-100 text-primary-500' : 'bg-gray-100 text-gray-600'
        }`}
      >
        {initials(name)}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 min-w-0">
            {owner && (
              <span className="shrink-0 w-2 h-2 rounded-full" style={{ background: owner.color }} title={`Owner: ${owner.name}`} />
            )}
            <span className={`truncate text-sm ${hasUnread ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}`}>
              {name}
            </span>
          </span>
          <span className="shrink-0 text-[11px] text-gray-400">{relativeTime(c.last_message_date)}</span>
        </div>

        {c.persona_label && (
          <div className="mt-0.5">
            <PersonaChip label={c.persona_label} role={c.persona_role} status={c.persona_status} size="xs" />
          </div>
        )}

        <div className="mt-0.5 flex items-center gap-1.5">
          <span className="shrink-0 text-gray-400">
            <ChannelIcon channel={c.channel} size={13} />
          </span>
          <span className={`truncate text-xs ${hasUnread ? 'text-gray-700' : 'text-gray-500'}`}>
            {c.last_message_direction === 'outbound' ? 'You: ' : ''}
            {truncate(c.last_message_body, 60) || '—'}
          </span>
          {hasUnread && (
            <span className="ml-auto shrink-0 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary-500 text-white text-[10px] font-semibold">
              {c.unread_count}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
